import {
  Alert,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  styled,
} from "@mui/material";
import TopNav from "../components/TopNav";
import pageStyle from "./CustomerDetails.module.css";
import { Save, Upload } from "@mui/icons-material";
import utilStyle from "./Utils.module.css";
import { ChangeEvent, useEffect, useState } from "react";
import {
  APIAddress,
  getBusinessAddress,
  getDefaultTaxId,
  getDefaultTerms,
  setBusinessAddress,
  setDefaultTaxId,
  setDefaultTerms,
  setLogo,
} from "../api/settings";
import { apiRequest } from "../utils";
import { APITax, getAllTaxes } from "../api/taxes";
import { APIDriveStatus, copyAllToDrive, getDriveStatus } from "../api/drive";

const VisuallyHiddenInput = styled("input")({
  clip: "rect(0 0 0 0)",
  clipPath: "inset(50%)",
  height: 1,
  overflow: "hidden",
  position: "absolute",
  bottom: 0,
  left: 0,
  whiteSpace: "nowrap",
  width: 1,
});

function SettingsPage() {
  useEffect(() => {
    getDefaultTerms().then((v) => setTerms(v));
    getDefaultTaxId().then((v) => setTaxId(v));
    getBusinessAddress().then((v) => setAddress(v));
    getAllTaxes().then((v) => setTaxesData(v));
    getDriveStatus()
      .then((v) => setDriveStatus(v))
      .catch(() => setDriveStatus(undefined));
  }, []);

  const [driveStatus, setDriveStatus] = useState<APIDriveStatus>();
  const [driveMessage, setDriveMessage] = useState("");

  // Poll while a Drive job is running so the result shows up without a reload.
  useEffect(() => {
    if (!driveStatus?.running) {
      return;
    }
    const timer = setTimeout(() => {
      getDriveStatus().then((v) => setDriveStatus(v));
    }, 3000);
    return () => clearTimeout(timer);
  }, [driveStatus]);

  const [taxesData, setTaxesData] = useState<APITax[]>([]);

  // Form fields
  const [terms, setTerms] = useState("");
  const [taxId, setTaxId] = useState<string>("");
  const [businessAddress, setAddress] = useState<APIAddress>({
    name: "",
    addressLine1: "",
    addressLine2: "",
    city: "",
    state: "",
    zip: "",
    phone: "",
    email: "",
    website: "",
  });

  // Handle changes
  const changeTerms = (change: ChangeEvent<HTMLInputElement>) => {
    setTerms(change.target.value);
    setDefaultTerms(change.target.value);
  };

  const changeTaxId = (newTaxId: string) => {
    setTaxId(newTaxId);
    setDefaultTaxId(newTaxId);
  };

  const setUpEmail = () => {
    const token = localStorage.getItem("token");
    apiRequest
      .get("/setup/gmail/start", {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((v) => {
        window.location.href = v.data.url;
      });
  };

  const copyToDrive = () => {
    setDriveMessage("");
    copyAllToDrive()
      .then(() => getDriveStatus())
      .then((v) => setDriveStatus({ ...v, running: true }))
      .catch((e) => {
        setDriveMessage(
          e.response?.status === 403
            ? "Only managers can copy data to Google Drive."
            : "Could not start copying to Google Drive."
        );
      });
  };

  const uploadImage = async (e: any) => {
    const file = e.target.files[0];
    const reader = new FileReader();
    reader.addEventListener(
      "load",
      () => {
        setLogo(reader.result as string);
      },
      false
    );
    reader.readAsDataURL(file);
  };

  const updateBusinessAddress = (update: Partial<APIAddress>) => {
    if (businessAddress) {
      const newBA = { ...businessAddress, ...update };
      setAddress(newBA);
      setBusinessAddress(newBA);
    }
  };

  return (
    <div className="page">
      <TopNav />
      <main>
        <form
          className={pageStyle.editCustomerForm}
          noValidate
          autoComplete="off"
        >
          <FormControl>
            <InputLabel htmlFor="termsInput">
              Default Terms and Conditions
            </InputLabel>
            <OutlinedInput
              onChange={changeTerms}
              value={terms}
              multiline
              minRows={3}
              id="termsInput"
              label="Default Terms and Conditions"
            />
          </FormControl>
          <FormControl sx={{ width: "100%" }}>
            <InputLabel>Default Tax</InputLabel>
            <Select
              onChange={(e) => changeTaxId(e.target.value)}
              value={taxId}
              label="Default Tax"
              sx={{ width: "100%" }}
            >
              {taxesData.map((t) => (
                <MenuItem key={t.id} value={t.id}>
                  {t.title} ({t.percentRate}%)
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </form>
        <form
          className={pageStyle.editCustomerForm}
          noValidate
          autoComplete="off"
        >
          <FormControl>
            <InputLabel htmlFor="nameInput">Business Name</InputLabel>
            <OutlinedInput
              onChange={(e) => updateBusinessAddress({ name: e.target.value })}
              value={businessAddress?.name}
              id="nameInput"
              label="Business Name"
            />
          </FormControl>
          <FormControl>
            <InputLabel htmlFor="addrLine1Input">
              Business Address Line 1
            </InputLabel>
            <OutlinedInput
              onChange={(e) =>
                updateBusinessAddress({ addressLine1: e.target.value })
              }
              value={businessAddress.addressLine1}
              id="addrLine1Input"
              label="Business Address Line 1"
            />
          </FormControl>
          <FormControl>
            <InputLabel htmlFor="addrLine2Input">
              Business Address Line 2
            </InputLabel>
            <OutlinedInput
              onChange={(e) =>
                updateBusinessAddress({ addressLine2: e.target.value })
              }
              value={businessAddress.addressLine2}
              id="addrLine1Input"
              label="Business Address Line 2"
            />
          </FormControl>
          <FormControl>
            <InputLabel htmlFor="cityInput">Business City</InputLabel>
            <OutlinedInput
              onChange={(e) => updateBusinessAddress({ city: e.target.value })}
              value={businessAddress.city}
              id="cityInput"
              label="Business City"
            />
          </FormControl>
          <FormControl>
            <InputLabel htmlFor="stateInput">Business State</InputLabel>
            <OutlinedInput
              onChange={(e) => updateBusinessAddress({ state: e.target.value })}
              value={businessAddress.state}
              id="stateInput"
              label="Business State"
            />
          </FormControl>
          <FormControl>
            <InputLabel htmlFor="zipInput">Business Zip</InputLabel>
            <OutlinedInput
              onChange={(e) => updateBusinessAddress({ zip: e.target.value })}
              value={businessAddress.zip}
              id="zipInput"
              label="Business Zip"
            />
          </FormControl>
          <FormControl>
            <InputLabel htmlFor="phoneInput">Business Phone</InputLabel>
            <OutlinedInput
              onChange={(e) => updateBusinessAddress({ phone: e.target.value })}
              value={businessAddress.phone}
              id="phoneInput"
              label="Business Phone"
            />
          </FormControl>
          <FormControl>
            <InputLabel htmlFor="emailInput">Business Email</InputLabel>
            <OutlinedInput
              onChange={(e) => updateBusinessAddress({ email: e.target.value })}
              value={businessAddress.email}
              id="emailInput"
              label="Business Email"
            />
          </FormControl>
          <FormControl>
            <InputLabel htmlFor="websiteInput">Business Website</InputLabel>
            <OutlinedInput
              onChange={(e) =>
                updateBusinessAddress({ website: e.target.value })
              }
              value={businessAddress.website}
              id="websiteInput"
              label="Business Website"
            />
          </FormControl>
        </form>
        <div className={utilStyle.controlHeader}>
          <div className={utilStyle.controlHeaderButtonContainer}>
            <Button
              component="label"
              role={undefined}
              variant="contained"
              tabIndex={-1}
              startIcon={<Upload />}
            >
              Upload Logo
              <VisuallyHiddenInput
                type="file"
                onChange={uploadImage}
                multiple
              />
            </Button>
            <Button onClick={setUpEmail} color="success" variant="contained">
              Connect Google (Email, Calendar, Drive)
            </Button>
          </div>
        </div>
        {driveStatus && (
          <div className={utilStyle.controlHeader}>
            <h3>Google Drive</h3>
            {!driveStatus.enabled ? (
              <Alert severity="info">
                Customers, photos and proposal PDFs are not being saved to
                Google Drive yet. Click "Connect Google" above and allow Drive
                access to turn it on.
              </Alert>
            ) : (
              <>
                <Alert severity="success">
                  New photos and proposal PDFs are saved to Google Drive, and
                  the customer sheet is updated after every sync. The sheet is
                  a copy: edit customers in the CRM, not in the sheet.
                </Alert>
                <div className={utilStyle.controlHeaderButtonContainer}>
                  {driveStatus.folderUrl && (
                    <Button
                      href={driveStatus.folderUrl}
                      target="_blank"
                      variant="outlined"
                    >
                      Open Drive Folder
                    </Button>
                  )}
                  {driveStatus.sheetUrl && (
                    <Button
                      href={driveStatus.sheetUrl}
                      target="_blank"
                      variant="outlined"
                    >
                      Open Customer Sheet
                    </Button>
                  )}
                  <Button
                    onClick={copyToDrive}
                    disabled={driveStatus.running}
                    variant="contained"
                  >
                    {driveStatus.running
                      ? "Copying to Drive..."
                      : "Copy Existing Data to Drive"}
                  </Button>
                </div>
                {!driveStatus.running && driveStatus.lastRun?.error && (
                  <Alert severity="warning">
                    Last Drive update had problems: {driveStatus.lastRun.error}.
                    Click "Copy Existing Data to Drive" to retry.
                  </Alert>
                )}
              </>
            )}
            {driveMessage && <Alert severity="error">{driveMessage}</Alert>}
          </div>
        )}
      </main>
    </div>
  );
}

export default SettingsPage;
